'use client';

import { useState, useEffect } from 'react';

import Image from 'next/image';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  BubbleInbound,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import { MagicalText } from '@/components/ui/magical-text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

type ComparisonStatus = 'best' | 'worst' | 'neutral';

interface ComparisonRow {
  metric: string;
  openAI: { value: string; status: ComparisonStatus };
  hubspot: { value: string; status: ComparisonStatus };
}

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitleA: 'Use Open directly in',
    heroTitleB: 'helpdesk',
    heroSubtitleA: 'No migration. Live in under an hour.',
    heroSubtitleB: 'All features from day one — no suite or tier required.',
    inboundTitle: 'Refund request · #4521',
    inboundPreview: "Hey, my order #4521 hasn't shipped — can I get a refund?",
    autopilotTitle: 'Autopilot · ticket #4521',
    autopilotSteps: [
      'Read ticket in HubSpot',
      'Pulled order from CRM',
      'Verified eligibility · 14 days old',
      'Refund issued · $89.00',
      'Closed in HubSpot · CSAT 5★',
    ],
    moPhotoAlt: 'Mo, Founder of Open',
    vs: 'vs',
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitleA: 'استخدم Open داخل نظام دعم',
    heroTitleB: 'اللي عندك',
    heroSubtitleA: 'بدون ترحيل. جاهز خلال أقل من ساعة.',
    heroSubtitleB: 'كل الميزات من أول يوم — بدون حزمة ولا باقة أعلى.',
    inboundTitle: 'طلب استرداد · #4521',
    inboundPreview: 'هلا، طلبي #4521 ما انشحن لحد الآن — أقدر أسترجع فلوسي؟',
    autopilotTitle: 'Autopilot · تذكرة #4521',
    autopilotSteps: [
      'قرأ التذكرة في HubSpot',
      'سحب الطلب من الـ CRM',
      'تأكّد من الأهلية · عمر الطلب ١٤ يوم',
      'رجّع المبلغ · $89.00',
      'أقفل التذكرة في HubSpot · CSAT 5★',
    ],
    moPhotoAlt: 'Mo، مؤسس Open',
    vs: 'مقابل',
  },
} as const;

// Companies that switched from HubSpot
const switchedCompanies = [
  {
    name: 'Qoyod',
    logo: '/logos/logo-qoyod.png',
    description: 'Leading accounting software in Saudi Arabia',
    automationRate: '72%',
  },
  {
    name: 'OCN',
    logo: '/logos/logo-ocn.png',
    description: 'Technology solutions provider',
    automationRate: '68%',
  },
];

function SwitchedFromHubSpot() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % switchedCompanies.length);
        setIsAnimating(false);
      }, 400);
    }, 2500);

    return () => clearInterval(interval);
  }, [isPaused]);

  const currentCompany = switchedCompanies[currentIndex];

  return (
    <div className="mt-20">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-col items-start gap-6 md:flex-row md:gap-8">
          {/* Mo's photo */}
          <div className="shrink-0">
            <div className="relative">
              <img
                src="/about/image.png"
                alt={c.moPhotoAlt}
                className="h-20 w-20 rounded-full object-cover shadow-xl ring-4 ring-white md:h-24 md:w-24"
              />
              <div className="absolute -right-1 -bottom-1 flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs">
                👋
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="flex-1">
            <blockquote className="text-lg leading-relaxed text-neutral-700 md:text-xl">
              <span className="font-semibold text-neutral-900">
                {t.helpdesk["Hey, I'm Mo, founder of Open."]}
              </span>{' '}
              {t.helpdesk["Don't take my word for it—companies like"]}{' '}
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="relative mx-2 inline-flex h-6 w-24 cursor-pointer items-center justify-center align-middle md:h-7 md:w-28"
                      style={{
                        verticalAlign: 'middle',
                        transform: 'translateY(-2px)',
                      }}
                      onMouseEnter={() => setIsPaused(true)}
                      onMouseLeave={() => setIsPaused(false)}
                    >
                      <span
                        className={cn(
                          'absolute inset-0 flex items-center justify-center transition-all duration-400',
                          isAnimating
                            ? 'translate-y-2 opacity-0'
                            : 'translate-y-0 opacity-100',
                        )}
                      >
                        <img
                          src={currentCompany.logo}
                          alt={currentCompany.name}
                          className="max-h-5 max-w-full object-contain md:max-h-6"
                        />
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs px-4 py-3">
                    <p className="font-semibold text-white">
                      {currentCompany.name}
                    </p>
                    <p className="mt-1 text-sm text-neutral-300">
                      {(t.logos as Record<string, string>)[
                        currentCompany.description
                      ] || currentCompany.description}
                    </p>
                    {currentCompany.automationRate && (
                      <p className="mt-1.5 text-xs text-green-400">
                        {currentCompany.automationRate}{' '}
                        {t.helpdesk['automation with Open']}
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>{' '}
              {
                t.helpdesk[
                  'chose Open over HubSpot AI. They did it because Open works better for them.'
                ]
              }{' '}
              <span className="font-medium text-neutral-900">
                {
                  t.helpdesk[
                    'No tier upgrades, no suite requirements—just results.'
                  ]
                }
              </span>
            </blockquote>
            <div className="mt-4 flex items-center gap-3">
              <span className="text-sm font-semibold text-neutral-900">
                Mo Gharbat
              </span>
              <span className="text-sm text-neutral-500">
                {t.helpdesk['Founder & CEO, Open']}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HubSpotPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData: ComparisonRow[] = [
    {
      metric: t.helpdesk['Setup Time'],
      openAI: { value: t.helpdesk['Under 1 hour'], status: 'best' },
      hubspot: { value: t.helpdesk['Complex config'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Integration'],
      openAI: { value: t.helpdesk['Native HubSpot user'], status: 'best' },
      hubspot: { value: t.helpdesk['Premium tiers only'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Avg Automation Rate'],
      openAI: { value: '77%', status: 'best' },
      hubspot: { value: '~40-50%', status: 'worst' },
    },
    {
      metric: t.helpdesk['Multi-modal AI'],
      openAI: {
        value: t.helpdesk['Voice, Vision, Text, Files'],
        status: 'best',
      },
      hubspot: { value: t.helpdesk['Text only (rest gated)'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Quality'],
      openAI: { value: t.helpdesk['Multi-model engine'], status: 'best' },
      hubspot: { value: t.helpdesk['ChatSpot AI'], status: 'neutral' },
    },
    {
      metric: t.helpdesk['AI Quality Assurance'],
      openAI: { value: t.helpdesk['Built-in automatic QA'], status: 'best' },
      hubspot: { value: t.helpdesk['Manual QA or add-on'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Agentic Workflows'],
      openAI: { value: t.helpdesk['Drag-and-drop builder'], status: 'best' },
      hubspot: { value: t.helpdesk['Tier-based limits'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Outbound Sequencing'],
      openAI: {
        value: t.helpdesk['WhatsApp, Phone, Email, SMS'],
        status: 'best',
      },
      hubspot: { value: t.helpdesk['Not available'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Knowledge Gap Detection'],
      openAI: {
        value: t.helpdesk['Auto-detects & suggests content'],
        status: 'best',
      },
      hubspot: { value: t.helpdesk['Manual identification'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Customer Insights'],
      openAI: {
        value: t.helpdesk['Auto Jira tickets, trends'],
        status: 'best',
      },
      hubspot: { value: t.helpdesk['Basic reporting'], status: 'neutral' },
    },
    {
      metric: t.helpdesk['Pricing Model'],
      openAI: { value: t.helpdesk['Outcome-based'], status: 'best' },
      hubspot: { value: t.helpdesk['High monthly fees'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Suite Requirement'],
      openAI: { value: t.helpdesk['Standalone'], status: 'best' },
      hubspot: { value: t.helpdesk['Full HubSpot suite'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Learning Capability'],
      openAI: { value: t.helpdesk['Continuous'], status: 'best' },
      hubspot: { value: t.helpdesk['Basic automation'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Observability'],
      openAI: { value: t.helpdesk['Full transparency'], status: 'best' },
      hubspot: { value: t.helpdesk['Limited visibility'], status: 'worst' },
    },
  ];

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={
          <>
            {c.heroTitleA}{' '}
            <span className="whitespace-nowrap">
              <Image
                src="/helpdicks/hubspot.png"
                alt="HubSpot"
                width={48}
                height={48}
                className="mr-1 inline-block h-[0.8em] w-[0.8em] rounded-[0.18em] align-[-0.1em]"
              />
              <span className="text-[#FF7A59]">HubSpot</span>
            </span>{' '}
            {c.heroTitleB}
          </>
        }
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/stories/fareharbor.mp4"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleInbound
                source="HubSpot"
                sourceLogo="/helpdicks/hubspot.png"
                title={c.inboundTitle}
                preview={c.inboundPreview}
              />
            ),
          },
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={c.autopilotTitle}
                steps={[...c.autopilotSteps]}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.helpdesk['Get started in minutes']}
        subtitle={
          t.helpdesk[
            'Simple setup, powerful AI. No suite required, no tier limits.'
          ]
        }
        learnMoreLink="#comparison"
        accentColor="orange"
        theme="light"
        benefits={[
          {
            title: t.helpdesk['Create a new user in HubSpot'],
            description:
              t.helpdesk[
                'Our AI will work like any other teammate in your HubSpot workspace.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/hubspot/create-the-ai-agent.mp4',
            imagePosition: 'right',
          },
          {
            title: t.helpdesk['Set the routing rules'],
            description:
              t.helpdesk[
                "Route 100% or 10% of your support — it's really up to you."
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/channels/hubspot/set-the-routing-rules.mp4',
            imagePosition: 'left',
          },
          {
            title: t.helpdesk['Monitor and iterate'],
            description:
              t.helpdesk[
                'Because your new AI works within HubSpot, all of your reporting and analytics remain intact. Continuously improve with feedback and training.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.helpdesk['Automation Rate'],
              sublabel: t.helpdesk['Industry-leading performance'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              <MagicalText>{t.helpdesk['Our AI']}</MagicalText> {c.vs}{' '}
              {t.helpdesk['HubSpot AI']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  "See how Open's AI engine delivers enterprise AI without the enterprise pricing."
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.helpdesk['Feature']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      <MagicalText>{t.helpdesk['Our AI']}</MagicalText>{' '}
                      {t.helpdesk['(Open)']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['HubSpot AI']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/30 hover:bg-muted/30 transition-colors"
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.openAI.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.openAI.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'openAI' &&
                            'bg-green-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('openAI')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.openAI.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.openAI.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.openAI.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.openAI.value}</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.hubspot.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.hubspot.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'hubspot' &&
                            'bg-red-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('hubspot')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.hubspot.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.hubspot.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.hubspot.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.hubspot.value}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Social Proof - Companies that chose Open over HubSpot AI */}
          <SwitchedFromHubSpot />
        </div>
      </section>

      {/* Automate HubSpot Manual Work Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Automate the manual tasks, not just the answers']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'In addition to fully resolving support tickets, Open automates the manual tasks that slow down your team—routing, tagging, QA, and more.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            systemFilter="HubSpot"
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.helpdesk[
            "Find answers to common questions about our HubSpot AI agent. Can't find what you're looking for? Contact our support team."
          ]
        }
        items={[
          {
            question:
              t.helpdesk['How long does it take to set up Open with HubSpot?'],
            answer:
              t.helpdesk[
                'Open can be set up in under an hour. It connects directly to your HubSpot Service Hub via API and starts resolving queries on day one using your existing help center content.'
              ],
          },
          {
            question:
              t.helpdesk['Do I need the full HubSpot suite to use Open?'],
            answer:
              t.helpdesk[
                "No. Unlike HubSpot's AI features which require expensive tier upgrades and the full suite, Open works standalone with your HubSpot Service Hub at any tier level."
              ],
          },
          {
            question:
              t.helpdesk[
                'Does Open work with HubSpot conversations and tickets?'
              ],
            answer:
              t.helpdesk[
                'Yes. Open integrates with HubSpot conversations, tickets, live chat, and email channels. It works within your existing HubSpot Service Hub setup without requiring any changes.'
              ],
          },
          {
            question:
              t.helpdesk['Can I test Open and HubSpot AI at the same time?'],
            answer:
              t.helpdesk[
                'Yes! We can set up Open for you in a way where you can run an A/B test and decide for yourself. Try it risk-free for 1 month.'
              ],
          },
          {
            question: t.helpdesk['Can I try Open risk-free?'],
            answer:
              t.helpdesk[
                'Absolutely. You can try Open risk-free for a month with free implementation. Our team will help you get set up and see results before you commit.'
              ],
          },
          {
            question: t.helpdesk["Can I use Open with HubSpot's free tier?"],
            answer:
              t.helpdesk[
                "Yes. Open works with any HubSpot tier, including the free tier. You don't need to upgrade your HubSpot subscription to access advanced AI capabilities."
              ],
          },
          {
            question:
              t.helpdesk[
                'How does Open pricing compare to HubSpot AI features?'
              ],
            answer:
              t.helpdesk[
                'Open costs $0.75-$0.90 per resolution with transparent pricing. HubSpot locks AI features behind premium tiers that can cost thousands per month, making Open significantly more affordable.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Can I see how Open is performing in HubSpot reports?'
              ],
            answer:
              t.helpdesk[
                'Yes. Open works as a native HubSpot agent, so all your existing dashboards, reports, and analytics continue to work. Track AI performance alongside your human agents in HubSpot.'
              ],
          },
          {
            question: t.helpdesk['How does Open hand off to human agents?'],
            answer:
              t.helpdesk[
                'Open updates HubSpot tickets, adds AI-generated summaries, and seamlessly hands off to human agents when needed—giving them full context to respond faster.'
              ],
          },
          {
            question: t.helpdesk['How does Open learn and improve over time?'],
            answer:
              t.helpdesk[
                "Open features continuous learning capabilities with built-in evaluation tools. It learns from your team's feedback, analyzes historical tickets, and improves accuracy over time with observability tools that provide full transparency."
              ],
          },
          {
            question: t.helpdesk['What channels can Open handle with HubSpot?'],
            answer:
              t.helpdesk[
                'Open is omnichannel and works across HubSpot conversations, tickets, live chat, email, voice, WhatsApp, and other messaging channels—all from one unified AI engine.'
              ],
          },
          {
            question:
              t.helpdesk[
                'Does Open require changes to my current HubSpot setup?'
              ],
            answer:
              t.helpdesk[
                'No. Open connects via API with no migration required. It works within your existing workflows, follows your assignment rules, and requires zero disruption to your current HubSpot configuration.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
